'use client'

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import Button from '@components/Button'

interface ModalProps {
  variant: 'danger' | 'cta' | 'success'
  show: boolean
  title: string
  message?: string
  children?: React.ReactNode
  icon?: React.ReactNode
  cancelLabel?: string
  buttonLabel?: string
  onConfirm?: () => void
  onClose: () => void
}
export default function Modal({
  variant,
  show,
  title,
  message,
  children,
  icon,
  cancelLabel,
  buttonLabel,
  onConfirm,
  onClose,
}: ModalProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(show)

  useEffect(() => {
    setOpen(show)
  }, [show])

  let iconStyle = ''
  let iconWrapper = ''
  switch (variant) {
    case 'danger':
      iconWrapper += 'bg-error/10'
      iconStyle += ' text-error'
      break
    case 'cta':
      iconWrapper += 'bg-cta/10'
      iconStyle += ' text-cta'
      break
    case 'success':
      iconWrapper += 'bg-success/10'
      iconStyle += ' text-success'
      break
  }

  function handleClose() {
    setOpen(false)
    onClose()
  }

  function handleConfirm() {
    if (onConfirm) {
      onConfirm()
    }
    setOpen(false)
  }

  return (
    <Dialog open={open} onClose={handleClose} className='relative z-50'>
      <DialogBackdrop
        transition
        className='fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in'
      />

      <div className='fixed inset-0 z-10 w-screen overflow-y-auto'>
        <div className='flex mt-32 items-end justify-center p-4 text-center sm:items-center sm:p-0'>
          <DialogPanel
            transition
            className='relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95'
          >
            <div className='sm:flex sm:items-start'>
              <div
                className={`mx-auto flex size-12 shrink-0 items-center justify-center rounded-full ${iconWrapper} sm:mx-0 sm:size-12`}
              >
                {icon || <ExclamationTriangleIcon aria-hidden='true' className={`size-6 ${iconStyle}`} />}
              </div>
              <div className='mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left'>
                <DialogTitle as='h3' className='text-base font-semibold text-gray-900'>
                  {title}
                </DialogTitle>
                {message && (
                  <div className='mt-2'>
                    <p className='text-sm text-gray-500'>{message}</p>
                  </div>
                )}
              </div>
            </div>
            {children && <div className='mt-4'>{children}</div>}
            <div className='flex flex-row justify-between mt-5'>
              <Button
                onClick={handleClose}
                variant='alt'
                label={
                  cancelLabel ??
                  (onConfirm ? t('content.components.modal.cancel_button') : t('content.components.modal.close_button'))
                }
              />
              {!!onConfirm && buttonLabel && <Button onClick={handleConfirm} variant={variant} label={buttonLabel} />}
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}
